// Single source of truth for the advertisement image requirements shown and validated in the UI.
//
// Max size: 5 MB. The ad image is sent as a base64 data-URI inside the JSON body of POST /api/ads
// (+33%, ~6.7 MB), which the backend accepts up to 8 MB on /api/ads only (server.js);
// ad.controller.js rejects larger images with a clear message. KEEP IN SYNC with
// MAX_AD_IMAGE_BYTES in backend2/controllers/ad.controller.js.
//
// Dimensions: the backend does not check dimensions. The customer app (qpay2) shows
//   banner   -> adCard.dart: 150 px high card, full width, BoxFit.cover      (~2.2:1 landscape)
//   fullpage -> fullpage_ad.dart: full screen, BoxFit.contain                (9:16 portrait)
// so the sizes below are recommendations; a different size is only warned about, never blocked.

export const AD_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const AD_IMAGE_ALLOWED_TYPES = ['image/jpeg', 'image/png'];
export const AD_IMAGE_TYPE_LABEL = 'JPG/PNG';
// Allowed relative difference of aspect ratio before warning
export const AD_IMAGE_RATIO_TOLERANCE = 0.1;

export const AD_IMAGE_SPECS = {
  banner: { width: 1080, height: 500, orientation: 'landscape' },
  fullpage: { width: 1080, height: 1920, orientation: 'portrait' },
};

export const formatBytes = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;

/** e.g. "Recommended 1080 x 1920 px (portrait), JPG/PNG, max 700 KB" */
export const adImageHint = (adType) => {
  const spec = AD_IMAGE_SPECS[adType];
  return `Recommended ${spec.width} x ${spec.height} px (${spec.orientation}), ${AD_IMAGE_TYPE_LABEL}, max ${formatBytes(AD_IMAGE_MAX_BYTES)}`;
};

const readDimensions = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve({ width: img.naturalWidth, height: img.naturalHeight }); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });

/**
 * Validate a chosen file. Resolves { ok, error, warning }.
 * ok=false -> reject (wrong type / too large / unreadable). warning -> accepted but dimensions differ.
 */
export const validateAdImage = async (file, adType) => {
  if (!AD_IMAGE_ALLOWED_TYPES.includes(file.type)) {
    return { ok: false, error: `Only ${AD_IMAGE_TYPE_LABEL} images are allowed.` };
  }
  if (file.size > AD_IMAGE_MAX_BYTES) {
    return {
      ok: false,
      error: `Image is ${formatBytes(file.size)}. Maximum allowed size is ${formatBytes(AD_IMAGE_MAX_BYTES)}.`,
    };
  }
  const dims = await readDimensions(file);
  if (!dims) return { ok: false, error: 'Could not read the image. Please choose a valid image file.' };

  const spec = AD_IMAGE_SPECS[adType];
  const ratio = dims.width / dims.height;
  const wanted = spec.width / spec.height;
  if (Math.abs(ratio - wanted) / wanted > AD_IMAGE_RATIO_TOLERANCE) {
    return {
      ok: true,
      warning: `Image is ${dims.width} x ${dims.height} px. Recommended is ${spec.width} x ${spec.height} px (${spec.orientation}); it may be cropped or shown with empty margins in the app.`,
    };
  }
  return { ok: true, dims };
};
